import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { useTranslation } from "react-i18next";
import type { CustomerCompanySelection } from "../_helpers/customer-company-selection";

type CompanyOption = {
  uuid: string;
  name: string;
};

type CustomerCompanySelectionListProps = {
  ariaLabel: string;
  companies: CompanyOption[];
  selection: CustomerCompanySelection;
  hasError: boolean;
  isLoading: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onToggleCompany: (companyUuid: string) => void;
  onToggleUnassignedCustomers: () => void;
};

export function CustomerCompanySelectionList({
  ariaLabel,
  companies,
  selection,
  hasError,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onToggleCompany,
  onToggleUnassignedCustomers,
}: CustomerCompanySelectionListProps) {
  const { t } = useTranslation(["common", "masterdata"]);

  return (
    <div aria-label={ariaLabel} className="border-input-border max-h-72 overflow-y-auto rounded-md border">
      <div className="divide-y">
        <label className="hover:bg-accent flex cursor-pointer items-center gap-3 px-3 py-2.5">
          <Checkbox
            checked={selection.includeUnassignedCustomers}
            onCheckedChange={onToggleUnassignedCustomers}
            aria-label={t("masterdata:CustomersWithoutCompany")}
          />
          <span className="italic">{t("masterdata:CustomersWithoutCompany")}</span>
        </label>
        {hasError ? (
          <p className="text-muted-foreground px-3 py-4 text-sm">{t("ErrorLoadingDataMessage")}</p>
        ) : isLoading && companies.length === 0 ? (
          <p className="text-muted-foreground px-3 py-4 text-sm">{t("Loading")}</p>
        ) : companies.length === 0 ? (
          <p className="text-muted-foreground px-3 py-4 text-sm">{t("NoCompaniesFound")}</p>
        ) : (
          companies.map((company) => (
            <label key={company.uuid} className="hover:bg-accent flex cursor-pointer items-center gap-3 px-3 py-2.5">
              <Checkbox
                checked={selection.companyUuids.includes(company.uuid)}
                onCheckedChange={() => onToggleCompany(company.uuid)}
                aria-label={company.name}
              />
              <span>{company.name}</span>
            </label>
          ))
        )}
      </div>
      {hasMore && (
        <Button
          variant={ButtonVariant.Ghost}
          colorVariant={ButtonColorVariant.Secondary}
          className="w-full justify-center"
          disabled={isLoadingMore}
          onClick={onLoadMore}
        >
          {isLoadingMore && <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />}
          {t("LoadMore")}
        </Button>
      )}
    </div>
  );
}
